import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  TUDÁSBÁZIS – generálás, letöltés, közzététel.
 *
 *  A felhasználói kézikönyv tisztán statikus oldal: HTML, CSS, kép – se adatbázis,
 *  se bejelentkezés. Ezért bárhová feltölthető, és ezért nincs mögötte külön
 *  szolgáltatás sem: a kiszolgáló állítja elő, kérésre.
 *
 *  A GENERÁLÁS a MOSTANI állapotból dolgozik. A menüket nem a kézikönyv tartja,
 *  hanem az adatbázis, intézményenként külön – ezért a generátor megkapja a futó
 *  rendszer képét, és abból:
 *    - kirajzolja, melyik szerepkör mit lát a bal menüben,
 *    - felveszi azokat a képernyőket, amelyek már léteznek, de még nincsenek leírva.
 *
 *  Az ELTÉRÉSEK ezért nem hibalista, hanem munkalista: mi az, amit a kézikönyv még
 *  nem mond el a rendszerről.
 */
@Component({
  selector: 'app-admin-tudasbazis',
  templateUrl: './tudasbazis.component.html',
  styleUrls: ['./tudasbazis.component.scss'],
})
export class AdminTudasbazisComponent implements OnInit {
  intezmeny = '';
  allapot: any = null;
  toltodik = true;
  fut = false;
  generalFut = false;
  eredmeny: any = null;
  reszletek = false;

  /*  AZ ADMIN FEJEZET ZÁRA.
   *
   *  A kézikönyv nyilvános, és az is marad – a szülőnek és a diáknak nem kell fiók.
   *  Az admin fejezet viszont azt írja le, hogyan lehet jogosultságot adni, más
   *  nevében belépni, tanúsítványt cserélni. Ha itt van jelszó, a generátor azt a
   *  fejezetet TITKOSÍTVA teszi a kész oldalra – nem elrejtve, hanem olvashatatlanul.
   *
   *  A meglévő jelszót a kiszolgáló SOSEM adja vissza, csak azt, hogy van-e; ezért a
   *  mező mindig üresen indul, és az üresen hagyása nem törlést jelent. */
  zar: any = null;
  zarJelszo = '';
  zarJelszo2 = '';
  zarFut = false;
  zarLathato = false;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const sajat = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = sajat?.intezmenyId || '';
    await this.betolt();
    await this.zarBetolt();
  }

  async zarBetolt() {
    try {
      this.zar = await firstValueFrom(this.http.get(`/api/tudasbazis/jelszo/${this.ut}`));
    } catch { this.zar = null; }
  }

  /**
   *  A jelszó beállítása. Kétszer kell beírni: a mező csillagozott, a rossz jelszó
   *  pedig csak a NYILVÁNOS oldalon derülne ki – ott, ahol már nincs mit javítani
   *  a bal menüből. Nyolc karakter az alsó határ, mert a titkosított fejezetet bárki
   *  letöltheti és offline próbálgathatja.
   */
  async zarMentes() {
    if (this.zarJelszo !== this.zarJelszo2) {
      this.data.sendToast('A két jelszó nem egyezik.', 'error');
      return;
    }
    if (this.zarJelszo.length < 8) {
      this.data.sendToast('A jelszó legyen legalább 8 karakter.', 'error');
      return;
    }
    await this.zarKuld(this.zarJelszo, 'Jelszó beállítva.');
  }

  /** A zár feloldása: üres jelszót küldünk, és a fejezet a következő generálástól nyilvános. */
  async zarTorles() {
    await this.zarKuld('', 'A zár feloldva – az admin fejezet a következő közzétételtől nyilvános.');
  }

  private async zarKuld(jelszo: string, uzenet: string) {
    this.zarFut = true;
    try {
      const r: any = await firstValueFrom(
        this.http.put(`/api/tudasbazis/jelszo/${this.ut}`, { jelszo }));
      this.zarJelszo = '';
      this.zarJelszo2 = '';
      this.zarLathato = false;
      await this.zarBetolt();
      await this.betolt();
      this.data.sendToast(`${uzenet} ${r?.figyelmeztetes || ''}`.trim(), 'success');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    } finally {
      this.zarFut = false;
    }
  }

  private get ut() { return encodeURI(this.intezmeny); }

  async betolt() {
    this.toltodik = true;
    try {
      this.allapot = await firstValueFrom(
        this.http.get(`/api/tudasbazis/allapot/${this.ut}`));
    } catch (e: any) {
      this.allapot = { elerheto: false, hiba: e?.error?.error || 'Az állapot nem kérhető le.' };
    } finally {
      this.toltodik = false;
    }
  }

  /** Emberi méret – a nyers bájtszámból nem derül ki, mekkora a csomag. */
  meret(b: number): string {
    if (!b) return '–';
    return b < 1024 * 1024 ? `${Math.round(b / 1024)} kB` : `${(b / 1024 / 1024).toFixed(2)} MB`;
  }

  /** A nyilvános cím teljes alakja – ezt lehet kimásolni és megosztani. */
  get nyilvanosCim(): string {
    const u = this.allapot?.nyilvanos_url || '';
    return u ? `${window.location.origin}${u}` : '';
  }

  /* ── eltérések ──────────────────────────────────────────────────────────
   *  A kiszolgáló szerepkörönként adja vissza; a képernyőn egy összesítés és egy
   *  kibontható lista a hasznos alak – a nyers JSON-t senki nem olvassa.
   */
  get elteresek(): any { return this.allapot?.elteresek || null; }

  get vanEltereses(): boolean {
    const o = this.elteresek?.osszesen;
    return !!o && (o.uj > 0 || o.megszunt > 0);
  }

  /** Szerepkörönként: mi az, ami már a rendszerben van, de a kézikönyvben nincs. */
  get ujKepernyok(): { szerep: string; tetelek: any[] }[] {
    const sz = this.elteresek?.szerepkorok || {};
    return Object.keys(sz)
      .map((k) => ({ szerep: sz[k].nev || k, tetelek: sz[k].uj || [] }))
      .filter((r) => r.tetelek.length);
  }

  get elavultSzakaszok(): { szerep: string; tetelek: any[] }[] {
    const sz = this.elteresek?.szerepkorok || {};
    return Object.keys(sz)
      .map((k) => ({ szerep: sz[k].nev || k, tetelek: sz[k].megszunt || [] }))
      .filter((r) => r.tetelek.length);
  }

  async masol() {
    try {
      await navigator.clipboard.writeText(this.nyilvanosCim);
      this.data.sendToast('A cím a vágólapra került.', 'success');
    } catch (e) {
      this.data.sendToast('A másolás nem sikerült – jelöld ki és másold kézzel.', 'warning');
    }
  }

  /**
   *  Generálás közzététel nélkül: meg lehet nézni, mi változna, mielőtt kimegy.
   */
  async general() {
    if (this.generalFut) return;
    this.generalFut = true;
    this.eredmeny = null;
    try {
      const r: any = await firstValueFrom(
        this.http.post(`/api/tudasbazis/generalas/${this.ut}`, {}));
      this.data.sendToast(`Elkészült: ${r?.darab || 0} fájl.`, 'success');
      await this.betolt();
      if (this.vanEltereses) this.reszletek = true;
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A generálás nem sikerült.', 'error');
    } finally {
      this.generalFut = false;
    }
  }

  /*  A letöltés a KISZOLGÁLÓN át megy: az frissen legenerálja a csomagot, tehát a
   *  letöltött ZIP soha nem régebbi, mint a rendszer állapota.
   */
  letoltes() {
    this.data.megnyitFajl(`/api/tudasbazis/zip/${this.ut}`, 'tudasbazis.zip');
  }

  async kozzetesz() {
    if (this.fut) return;
    if (!confirm('Legenerálod és közzéteszed a tudásbázist a rendszer fájltárába?\n\n'
      + 'A kézikönyv a mostani állapotból készül (menük, jogosultságok), '
      + 'a meglévő fájlok felülíródnak, és a nyilvános cím azonnal az újat mutatja.')) return;
    this.fut = true;
    this.eredmeny = null;
    try {
      this.eredmeny = await firstValueFrom(
        this.http.post(`/api/tudasbazis/kozzetetel/${this.ut}`, {}));
      this.data.sendToast(`Közzétéve: ${this.eredmeny?.feltoltve || 0} fájl.`, 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A közzététel nem sikerült.', 'error');
    } finally {
      this.fut = false;
    }
  }
}
